type RhythmLegendItem = {
  label: string;
  tone: 'primary' | 'tint';
};

type RhythmEndMarker = {
  label: string;
  caption: string;
};

type RhythmBarChartProps = {
  oddLabel: string;
  evenLabel: string;
  legends: [RhythmLegendItem, RhythmLegendItem];
  endMarker?: RhythmEndMarker;
  ariaLabel: string;
};

const toneClasses = {
  primary: 'bg-primary text-primary-foreground',
  tint: 'bg-primary/15 text-foreground',
} as const;

const RhythmBarChart = ({ oddLabel, evenLabel, legends, endMarker, ariaLabel }: RhythmBarChartProps) => {
  const gridColumns = endMarker ? 'grid-cols-[repeat(10,minmax(0,1fr))_1.6fr]' : 'grid-cols-10';

  return (
    <div className="mt-4" role="img" aria-label={ariaLabel}>
      <div className={`grid h-[128px] items-end gap-1.5 sm:h-[190px] ${gridColumns}`}>
        {Array.from({ length: 10 }, (_, index) => {
          const isOddWeek = index % 2 === 0;
          return (
            <div
              key={index}
              className={`flex h-full items-end justify-center rounded-t-lg rounded-b pb-2 text-xs font-medium sm:pb-2 ${isOddWeek ? toneClasses.primary : `max-h-[78%] ${toneClasses.tint}`}`}
            >
              {isOddWeek ? oddLabel : evenLabel}
            </div>
          );
        })}
        {endMarker && (
          <div className="flex h-full items-center justify-center rounded-t-lg rounded-b bg-foreground px-1 text-center text-[9px] font-medium leading-tight text-accent sm:text-xs">
            {endMarker.label}
          </div>
        )}
      </div>

      <div className={`mt-1.5 grid gap-1.5 text-center text-[9px] text-muted-foreground sm:text-xs ${gridColumns}`}>
        {Array.from({ length: 10 }, (_, index) => <span key={index}>W{index + 1}</span>)}
        {endMarker && <span className="leading-tight">{endMarker.caption}</span>}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
        {legends.map(({ label, tone }) => (
          <span key={label} className="flex items-center gap-2">
            <span aria-hidden="true" className={`h-3.5 w-3.5 rounded ${toneClasses[tone]}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default RhythmBarChart;
